import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Plus, BookOpen, Trash2, Edit, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useNavigate } from 'react-router-dom';

function QuestionBanks() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';
  const isFaculty = user.role === 'faculty';
  const canDownload = isSuperAdmin;
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBank, setCurrentBank] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    tags: '',
    description: '',
  });

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const response = await api.get('/question-banks');
      setBanks(response.data);
    } catch (error) {
      console.error('Error fetching banks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
      };
      
      if (editMode && currentBank) {
        await api.put(`/question-banks/${currentBank.id}`, data);
        toast.success('Question bank updated successfully');
      } else {
        await api.post('/question-banks', data);
        toast.success('Question bank created successfully');
      }
      
      setOpen(false);
      resetForm();
      fetchBanks();
    } catch (error) {
      console.error('Error saving bank:', error);
      toast.error('Failed to save question bank');
    }
  };

  const handleEdit = (bank) => {
    setCurrentBank(bank);
    setFormData({
      title: bank.title,
      subject: bank.subject,
      tags: bank.tags?.join(', ') || '',
      description: bank.description || '',
    });
    setEditMode(true);
    setOpen(true);
  };

  const resetForm = () => {
    setFormData({ title: '', subject: '', tags: '', description: '' });
    setCurrentBank(null);
    setEditMode(false);
  };

  const handleDelete = async (bankId) => {
    if (!window.confirm('Are you sure you want to delete this question bank?')) return;
    try {
      await api.delete(`/question-banks/${bankId}`);
      toast.success('Question bank deleted');
      fetchBanks();
    } catch (error) {
      console.error('Error deleting bank:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDownloadCSV = async (bankId, bankTitle) => {
    try {
      const response = await api.get(`/question-banks/${bankId}/export/csv`);
      const { data, filename } = response.data;
      
      // Decode base64 and download
      const csvContent = atob(data);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Downloaded ${response.data.question_count} questions`);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to download CSV');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="question-banks-title">Question Banks</h1>
            <p className="text-gray-600">Manage your question repositories</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            {!isFaculty && (
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-bank-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Bank
                </Button>
              </DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editMode ? 'Edit' : 'Create'} Question Bank</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    data-testid="bank-title-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    data-testid="bank-subject-input"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    data-testid="bank-tags-input"
                    placeholder="math, algebra, advanced"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    data-testid="bank-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="submit-bank-button">
                    {editMode ? 'Update' : 'Create'} Bank
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {banks.map((bank) => (
            <Card
              key={bank.id}
              className="p-6 card-hover relative"
              data-testid={`bank-card-${bank.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center flex-1" onClick={() => navigate(`/admin/question-banks/${bank.id}`)} style={{cursor: 'pointer'}}>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">{bank.title}</h3>
                    <p className="text-sm text-gray-500">{bank.subject}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {canDownload && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadCSV(bank.id, bank.title);
                    }}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    title="Download CSV"
                    data-testid={`download-bank-${bank.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  )}
                  {!isFaculty && (
                  <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(bank);
                    }}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    data-testid={`edit-bank-${bank.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(bank.id);
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-bank-${bank.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  </>
                  )}
                </div>
              </div>
              <div onClick={() => navigate(`/admin/question-banks/${bank.id}`)}>
                <p className="text-sm text-gray-600 mb-4">{bank.description}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{bank.question_count} questions</span>
                  {bank.tags && bank.tags.length > 0 && (
                    <div className="flex gap-2">
                      {bank.tags.slice(0, 2).map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {banks.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No question banks yet</h3>
            <p className="text-gray-500 mb-4">Create your first question bank to get started</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default QuestionBanks;